import React, { useMemo, useState } from "react";

import { TFunction } from "utils";

import { YEAR_TYPE } from "models";

import { ContextMenu, Icon, ItemProps, Modal } from "components";

export const CropExplorerTimelineYearHeader = ({
  year,
  t,
  hasEntries,
  yearlyYears,
  baselineYears,
  previousYearHasEntries,
  nextYearHasEntries,
  allowDuplicateToPreviousYear = false,
  allowDuplicateToNextYear = false,
  onYearAdded,
  onYearDuplicated,
  onYearDeleted,
}: {
  year: number;
  t: TFunction;
  type: YEAR_TYPE;
  yearlyYears?: number[];
  baselineYears?: number[];
  hasEntries: boolean;
  previousYearHasEntries: boolean;
  nextYearHasEntries: boolean;
  allowDuplicateToPreviousYear?: boolean;
  allowDuplicateToNextYear?: boolean;
  onYearAdded: () => void;
  onYearDuplicated: (sourceYear: number, targetYear: number) => void;
  onYearDeleted: (year: number, deleteYear: boolean) => void;
}) => {
  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const [showYearDeletionModal, setShowYearDeletionModal] = useState<boolean>(false);
  const [showYearDuplicationModal, setShowYearDuplicationModal] = useState<boolean>(false);
  const [targetYear, setTargetYear] = useState<number>();
  const [shouldDeleteYear, setShouldDeleteYear] = useState<boolean>(false);

  const contextMenuPlacement = (year: number) => {
    if (baselineYears) {
      const bauYears = [...baselineYears];
      bauYears.sort();
      if (bauYears[0] === year) {
        return "bottom-right";
      }
    }
    return "bottom-left";
  };

  const contextMenuItems = useMemo(() => {
    const items: ItemProps[] = [];

    // handle duplicate to next and previous year
    if (hasEntries) {
      if (allowDuplicateToPreviousYear)
        items.splice(1, 0, {
          iconName: "tabler/copy",
          label: t("timeline.headers.contextMenu.duplicateToPreviousYear"),
          onClick: previousYearHasEntries
            ? () => {
                setTargetYear(year - 1);
                setShowYearDuplicationModal(true);
              }
            : () => onYearDuplicated(year, year - 1),
        });

      if (allowDuplicateToNextYear)
        items.splice(2, 0, {
          iconName: "tabler/copy",
          label: t("timeline.headers.contextMenu.duplicateToNextYear"),
          onClick: nextYearHasEntries
            ? () => {
                setTargetYear(year + 1);
                setShowYearDuplicationModal(true);
              }
            : () => onYearDuplicated(year, year + 1),
        });

      items.splice(3, 0, {
        iconName: "tabler/trash",
        label: t("timeline.headers.contextMenu.deleteCrops"),
        onClick: () => {
          setShowYearDeletionModal(true);
          setShouldDeleteYear(false);
        },
        state: "warning",
      });
    }

    // handle empty years for baseline years
    if (baselineYears && !hasEntries)
      items.splice(3, 0, {
        iconName: "tabler/trash",
        label: t("timeline.headers.contextMenu.deleteCrops"),
        state: "disabled",
      });

    if (yearlyYears) {
      const futureYears = [...yearlyYears];

      const yearIndex = futureYears.findIndex((value) => value === year);

      // handle "Add year" action
      if (futureYears.length < 5) {
        items.splice(0, 0, {
          iconName: "klim/plus",
          label: t("timeline.headers.contextMenu.addYear"),
          onClick: onYearAdded,
        });
      }

      // handle future crops with empty years
      if (yearIndex !== futureYears.length - 1 && !hasEntries)
        items.splice(4, 0, {
          iconName: "tabler/trash",
          label: t("timeline.headers.contextMenu.deleteYear"),
          state: "disabled",
        });

      // handle delete the last future crops year except first future crops year
      if (yearIndex !== 0 && yearIndex === futureYears.length - 1)
        items.splice(4, 0, {
          iconName: "tabler/trash",
          label: t("timeline.headers.contextMenu.deleteYear"),
          onClick: () => {
            setShowYearDeletionModal(true);
            setShouldDeleteYear(true);
          },
          state: "warning",
        });
    }

    return items;
  }, [
    allowDuplicateToNextYear,
    allowDuplicateToPreviousYear,
    hasEntries,
    nextYearHasEntries,
    onYearAdded,
    onYearDuplicated,
    previousYearHasEntries,
    t,
    year,
    yearlyYears,
    baselineYears,
  ]);

  return (
    <div data-testid={`crop-explorer-year-header-${year}`} className="row-space-between padding-left-100 padding-right-100">
      <span className="year">{year}</span>
      <ContextMenu
        dataTestId={`crop-explorer-year-context-menu-${year}`}
        items={contextMenuItems}
        placement={contextMenuPlacement(year)}
        visible={showContextMenu}
        onClose={() => setShowContextMenu(false)}
      >
        <Icon
          dataTestId={`crop-explorer-year-context-menu-${year}-button`}
          iconName="tabler/dots-vertical"
          stroke="#6d6d6d"
          fill="#6d6d6d"
          onClick={() => setShowContextMenu(true)}
        />
      </ContextMenu>
      <Modal
        dataTestId={`crop-exploration-duplicate-year-modal-${year}`}
        cancelText={t("timeline.duplicateYearModal.cancel")}
        description={t("timeline.duplicateYearModal.description", { year: targetYear })}
        onCancel={() => setShowYearDuplicationModal(false)}
        okText={t("timeline.duplicateYearModal.confirm")}
        onOk={() => {
          setShowYearDuplicationModal(false);
          if (targetYear) onYearDuplicated(year, targetYear);
        }}
        showModal={showYearDuplicationModal}
        title={t("timeline.duplicateYearModal.title")}
      />
      <Modal
        cancelText={t("timeline.deleteYearModal.cancel")}
        description={t("timeline.deleteYearModal.description", { year })}
        onCancel={() => setShowYearDeletionModal(false)}
        okText={t("timeline.deleteYearModal.confirm")}
        onOk={() => {
          setShowYearDeletionModal(false);
          onYearDeleted(year, shouldDeleteYear);
        }}
        showModal={showYearDeletionModal}
        title={t("timeline.deleteYearModal.title")}
      />
    </div>
  );
};
